import { Box, Skeleton } from "@mui/material";
import React, { useEffect, useState } from "react";
import { getFirstDayOfMonth, getUnixTimestamp } from "../../utils";

import ChevronDownIcon from "../Icons/ChevronDownVisit";
import { IOptionGeneric } from "../../shared/interface";
import SelectableTimes from "../SelectableTimes/SelectableTimes";
import StaticBookingCalendar from "../StaticBookingCalendar/StaticBookingCalendar";
import { fromUnixToDateString } from "../../utils/unix-timestamp";
import { useAppointmentStore } from "../../hooks/redux-hooks/appointment-store/appointment.store";
import { useAvailableTimes } from "../../services/available-times/available-times.service";
import { useModal } from "../../context/ModalContext";
import { useUserDetail } from "../../services/user-detail/user-detail.service";
import useWebsiteUserAppointment from "../../services/website-user-appointment/website-user-appointment.service";
import { ASSIGNED_BOOKING_TYPE, MONTHS_NAME } from "../../config/params";

interface IAvailableTimeSlots {
  title?: string;
  date?: number;
  isSelectable?: boolean;
  icon?: React.ElementType<any>;
  isCarousal?: boolean;
  isPreviewModal?: boolean;
}

const AvailableTimeSlotsNew: React.FC<IAvailableTimeSlots> = ({
  title,
  icon,
  date,
  isSelectable,
  isCarousal = true,
  isPreviewModal,
}) => {
  const { user } = useUserDetail();
  const todayEpoch = getUnixTimestamp(new Date());
  const [selectedDateEpochFormat, setSelectedDateEpochFormat] =
    useState<number>();
  const { modalView } = useModal();
  const { reasonForVisit, setAppointmentsForm, selectedPets, appointmentForm } =
    useAppointmentStore();
  const [autoMonthChangeAttempts, setAutoMonthChangeAttempts] = useState(0);
  const { getUserAppointment, webUserAppointment } =
    useWebsiteUserAppointment();
  const {
    getAvailableTimes,
    timesList: availableTimesSlots,
    loading,
    daysOfMonthList: fullMonthDaysList,
  } = useAvailableTimes();
  const unixDateRef = React.useRef<number | null>(null);
  const [selectedDate, setSelectedDate] = useState<string>("");
  const [disableSkip, setDisableSkip] = useState<boolean>(false);

  function getFormattedDateFromEpoch(epoch: number): string {
    const date = new Date(fromUnixToDateString(epoch, "GMT"));
    const options: Intl.DateTimeFormatOptions = {
      month: "long",
      day: "numeric",
    };

    return date.toLocaleDateString("en-US", options);
  }

  const handleDateSelected = (date: Date) => {
    handleDateSelection(date);
    setSelectedDateEpochFormat(getUnixTimestamp(date));
    // setIsMonthChange(false);
    unixDateRef.current = getUnixTimestamp(date);
  };

  const handleDateSelection = (date: Date) => {
    setAppointmentsForm(null);
    setSelectedDate(MONTHS_NAME[date.getMonth()] + " " + date.getDate());
  };

  // when user land on this page finding if user coming from
  // website and selected slot on website or not
  useEffect(() => {
    if (localStorage.getItem("isWebUser") !== "undefined") {
      const isWebUser = JSON.parse(localStorage.getItem("isWebUser") || "{}");

      modalView && localStorage.setItem("isWebUser", "{}");
      if (isWebUser?.nextScreen === "bookAVisit") {
        getUserAppointment();
      }
    }
  }, []);

  // when user change location then we need to show updated location slots
  // and when user change date then automatically call api for slots
  useEffect(() => {
    if (user?.location?.idLocation) {
      getAvailableTimes({
        reasons: { ...reasonForVisit },
        date: selectedDateEpochFormat?.toString()!,
        isPreviewModal,
      });
    }
  }, [selectedDateEpochFormat, user?.location, selectedPets.length]);

  // When number of pets change then call available time api
  useEffect(() => {
    if (user?.location?.idLocation && selectedDateEpochFormat) {
      getAvailableTimes({
        reasons: { ...reasonForVisit },
        date: selectedDateEpochFormat?.toString()!,
        isPreviewModal,
      });
    }
  }, [selectedPets.length]);

  const handleSlotSelection = (option: IOptionGeneric) => {
    if (user?.location?.idLocation && option) {
      setAppointmentsForm({
        time: option.value,
        location: user?.location?.idLocation,
        resourcesId: option.resourcesId,
        selectedSlotLabel: option.label,
        day: selectedDate,
      });
    }
  };

  const handleMonthChange = (newMonth: Date) => {
    const dateUnix = getUnixTimestamp(getFirstDayOfMonth(newMonth));

    setSelectedDateEpochFormat(dateUnix);
    // setIsMonthChange(true);
  };

  // this useEffect responsible for fetchng slot if not found on current
  // date or current month
  useEffect(() => {
    if (
      !disableSkip &&
      fullMonthDaysList.length &&
      !availableTimesSlots.length
    ) {
      const nearestAvailableDate = fullMonthDaysList.filter((day: any) => {
        return !day.disabled;
      });

      if (nearestAvailableDate.length) {
        setSelectedDateEpochFormat(+nearestAvailableDate[0].value);
        setDisableSkip(true);
      }
      if (
        autoMonthChangeAttempts < 5 &&
        selectedDateEpochFormat &&
        !nearestAvailableDate.length
      ) {
        /* instead of using getMonth/ getFullYear,
         we can use getUTCMonth/ getUTCFullYear to avoid timeZone issue
         which is causing our month to not move forward and getting stuck
          after 1-2 iteration */

        const currentDate = new Date(selectedDateEpochFormat * 1000);
        const currentMonth = currentDate.getUTCMonth();
        const nextMonth = currentMonth + 1;

        const nextYear =
          nextMonth > 11 ?
            currentDate.getUTCFullYear() + 1 :
            currentDate.getUTCFullYear();

        const nextMonthFirstDate = new Date(nextYear, nextMonth % 12, 1);

        setAutoMonthChangeAttempts(autoMonthChangeAttempts + 1);
        handleMonthChange(nextMonthFirstDate);
      }
    }
  }, [fullMonthDaysList, availableTimesSlots]);

  // this useEffect set initial date to fetch slots if user coming back step
  // from reason for visit or from pet selection
  useEffect(() => {
    if (appointmentForm?.time) {
      setSelectedDateEpochFormat(+appointmentForm?.time);
    } else {
      setSelectedDateEpochFormat(date ? date : todayEpoch);
    }
  }, []);

  // this useEffect responsible for fetching data of that date that
  // user already selected on website
  useEffect(() => {
    if (webUserAppointment) {
      // eslint-disable-next-line max-len
      (webUserAppointment.appointmentCreateType !== ASSIGNED_BOOKING_TYPE.DROPOFF_BOOKING) && setSelectedDateEpochFormat(webUserAppointment.start_at);
    }
  }, [webUserAppointment]);

  // setting selected date to redux appointment form
  // to show on panel in booking flow
  useEffect(() => {
    if (selectedDateEpochFormat) {
      setSelectedDate(getFormattedDateFromEpoch(+selectedDateEpochFormat));
    } else if (selectedDateEpochFormat) {
      setSelectedDate(getFormattedDateFromEpoch(+selectedDateEpochFormat));
    }
  }, [selectedDateEpochFormat]);

  // useEffect handling webuser slot selection
  useEffect(() => {
    if (webUserAppointment) {
      const slotSelectedOnWebsite = availableTimesSlots.find(
        (data: IOptionGeneric) => +data.value == webUserAppointment.start_at,
      );

      slotSelectedOnWebsite && handleSlotSelection(slotSelectedOnWebsite);
    }
  }, [webUserAppointment, availableTimesSlots]);

  return (
    <Box className="grow flex flex-col">
      <div className="flex flex-col xl:flex-row gap-2">
        <StaticBookingCalendar
          onSelected={handleDateSelected}
          icon={icon ? icon : ChevronDownIcon}
          fullMonthDaysList={fullMonthDaysList}
          currentDay={selectedDateEpochFormat}
          handleMonthChange={handleMonthChange}
          loading={loading}
        />
        <Box className="h-full w-full overflow-y-scroll scrollbar-thin max-h-[18.5rem] px-2">
          {!loading && availableTimesSlots?.length ? (
            <SelectableTimes
              value={appointmentForm?.time}
              options={availableTimesSlots}
              onSelected={(option: IOptionGeneric) => {
                handleSlotSelection(option);
              }}
              isSelectable={isSelectable}
              carousel={false}
              isDashboard={true}
            />
          ) : (
            <div className="flex h-full w-full justify-center items-center">
              {!loading ? (
                <div className="text-sm text-gray-1 py-4">
                    We’re fully booked for this day.
                </div>
              ) : (
                <div className="flex flex-col justify-around w-full h-full gap-2">
                  <Skeleton
                    variant="rectangular"
                    animation="wave"
                    className="rounded-xl w-full !h-[1.5rem]"
                  />
                  <Skeleton
                    variant="rectangular"
                    animation="wave"
                    className="rounded-xl w-full !h-[1.5rem]"
                  />
                  <Skeleton
                    variant="rectangular"
                    animation="wave"
                    className="rounded-xl w-full !h-[1.5rem]"
                  />
                  <Skeleton
                    variant="rectangular"
                    animation="wave"
                    className="rounded-xl w-full !h-[1.5rem]"
                  />
                  <Skeleton
                    variant="rectangular"
                    animation="wave"
                    className="rounded-xl w-full !h-[1.5rem]"
                  />
                  <Skeleton
                    variant="rectangular"
                    animation="wave"
                    className="rounded-xl w-full !h-[1.5rem]"
                  />
                  <Skeleton
                    variant="rectangular"
                    animation="wave"
                    className="rounded-xl w-full !h-[1.5rem]"
                  />
                </div>
              )}
            </div>
          )}
        </Box>
      </div>
    </Box>
  );
};

export default AvailableTimeSlotsNew;
